import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { ApiKeySettings } from './components/ApiKeySettings';
import { ManagementDashboard } from './components/ManagementDashboard';
import UsageDashboard from './components/UsageDashboard';
import { EmployeeMapping } from './components/EmployeeMapping';
import { Breadcrumb } from './components/Breadcrumb';
import {
  ADMIN_CREDENTIAL_REVISION_STORAGE_KEY,
  ANTHROPIC_ADMIN_API_KEY_STORAGE_KEY,
  getAnthropicApiKey,
  getThemeMode,
  getTimezoneMode,
  hasApiKey,
  OPENAI_ADMIN_API_KEY_STORAGE_KEY,
  readAdminCredentialRevision,
  saveThemeMode,
  saveTimezoneMode
} from './utils/storage';
import type { TimezoneMode, ThemeMode } from './utils/storage';
import { getCacheManager } from './utils/cache-service';
import { performMigration, checkMigrationRequired } from './utils/cache-migration';
import { NotificationDropdown } from './components/NotificationDropdown';
import { TabButton } from '@/components/ui/TabButton';
import { ErrorBoundary, SectionErrorBoundary } from './components/ui/ErrorBoundary';
import { useDailyCostMonitor } from './hooks/useDailyCostMonitor';
import type { DailyAlertNotification } from './types/daily-alert';
import { formatUtcDateKey, type DailyBudgetEvaluation } from './utils/daily-user-budget';
import {
  applyDailyDismissals,
  parseStoredDailyDismissals,
  parseStoredDailyNotifications,
  pruneDailyDismissals,
  reconcileDailyNotifications
} from './utils/daily-alert-notifications';
import { DEFAULT_DAILY_ALERT_THRESHOLD_USD } from './constants/ui';
import { debugLog } from './utils/debug-log';



// Tab definitions
const TABS = {
  USAGE: 'usage',
  MANAGEMENT: 'management',
  GITHUB: 'github',
  GEMINI: 'gemini',
  EMPLOYEE_MAPPING: 'employee-mapping'
};

// Backward-compatible redirects for removed tabs
const TAB_REDIRECTS: Record<string, string> = {
  'openai': TABS.USAGE,
  'anthropic': TABS.USAGE,
  'anthropic-usage': TABS.USAGE,
  'anthropic-management': TABS.USAGE,
};

// Tab components defined at module level to prevent recreation on parent re-render

interface UsageTabProps {
  hasValidApiKey: boolean;
  hasAnthropicKey: boolean;
  credentialRevision: string;
  onOpenSettings: () => void;
  timezoneMode: TimezoneMode;
  onToggleTimezone: () => void;
}

const UsageTab: React.FC<UsageTabProps> = ({ hasValidApiKey, hasAnthropicKey, credentialRevision, onOpenSettings, timezoneMode, onToggleTimezone }) => {
  if (!hasValidApiKey && !hasAnthropicKey) {
    return (
      <div className="text-center p-6 lg:p-8">
        <h2 className="text-lg lg:text-xl font-bold mb-4 text-gray-900 dark:text-white">
          Usage Dashboard
        </h2>
        <p className="text-sm lg:text-base text-gray-600 dark:text-gray-300 mb-4">
          Configure an OpenAI or Anthropic Admin API key in Settings to view usage data.
        </p>
        <Button
          onClick={onOpenSettings}
          className="bg-blue-600 hover:bg-blue-700 text-sm lg:text-base"
        >
          Open Settings
        </Button>
      </div>
    );
  }
  return (
    <UsageDashboard
      hasOpenAiKey={hasValidApiKey}
      hasAnthropicKey={hasAnthropicKey}
      credentialRevision={credentialRevision}
      timezoneMode={timezoneMode}
      onToggleTimezone={onToggleTimezone}
    />
  );
};

interface ManagementAPITabProps {
  hasValidApiKey: boolean;
  onOpenSettings: () => void;
}

const ManagementAPITab: React.FC<ManagementAPITabProps> = ({ hasValidApiKey, onOpenSettings }) => {
  if (!hasValidApiKey) {
    return (
      <div className="text-center p-6 lg:p-8">
        <h2 className="text-lg lg:text-xl font-bold mb-4 text-gray-900 dark:text-white">
          Management API Access
        </h2>
        <p className="text-sm lg:text-base text-gray-600 dark:text-gray-300 mb-4">
          Admin API Key is not configured.
        </p>
        <Button
          onClick={onOpenSettings}
          className="bg-blue-600 hover:bg-blue-700 text-sm lg:text-base"
        >
          Configure API Key in Settings
        </Button>
      </div>
    );
  }
  return <ManagementDashboard />;
};

// Apply theme class to <html> element
const applyTheme = (mode: ThemeMode) => {
  if (mode === 'dark') {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
};

// Initialize theme from localStorage before first render
applyTheme(getThemeMode());

const readNotificationThreshold = (): number => {
  const saved = localStorage.getItem('notificationThreshold');
  if (saved === null) return DEFAULT_DAILY_ALERT_THRESHOLD_USD;
  const parsed = Number(saved);
  return Number.isFinite(parsed) && parsed >= 0
    ? parsed
    : DEFAULT_DAILY_ALERT_THRESHOLD_USD;
};

const DISMISSED_NOTIFICATIONS_STORAGE_KEY = 'dailyAlertDismissalsV1';

const readStoredNotifications = (): DailyAlertNotification[] =>
  applyDailyDismissals(
    parseStoredDailyNotifications(localStorage.getItem('apiKeyNotifications')),
    parseStoredDailyDismissals(localStorage.getItem(DISMISSED_NOTIFICATIONS_STORAGE_KEY))
  );

// Main component
const App = () => {
  // Theme management
  const [themeMode, setThemeMode] = useState<ThemeMode>(getThemeMode);

  const handleToggleTheme = useCallback(() => {
    setThemeMode(prev => {
      const next = prev === 'dark' ? 'light' : 'dark';
      saveThemeMode(next);
      applyTheme(next);
      return next;
    });
  }, []);

  // Tab state with URL query parameter support
  const [activeTab, setActiveTab] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tabFromUrl = urlParams.get('tab');
    if (tabFromUrl) {
      // Handle redirects for removed tabs
      if (TAB_REDIRECTS[tabFromUrl]) return TAB_REDIRECTS[tabFromUrl];
      if (Object.values(TABS).includes(tabFromUrl)) return tabFromUrl;
    }
    return TABS.USAGE;
  });
  const [hasValidApiKey, setHasValidApiKey] = useState(false);
  const [hasAnthropicKey, setHasAnthropicKey] = useState(false);
  const [credentialRevision, setCredentialRevision] = useState(readAdminCredentialRevision);
  const [appInitialized, setAppInitialized] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [timezoneMode, setTimezoneMode] = useState<TimezoneMode>(getTimezoneMode);
  const [notifications, setNotifications] = useState<DailyAlertNotification[]>(readStoredNotifications);
  const [notificationThreshold, setNotificationThreshold] = useState(readNotificationThreshold);
  const notificationsRef = useRef(notifications);
  notificationsRef.current = notifications;

  const refreshAdminCredentialState = useCallback(() => {
    setHasValidApiKey(hasApiKey());
    setHasAnthropicKey(!!getAnthropicApiKey());
    setCredentialRevision(readAdminCredentialRevision());
  }, []);

  // Check for API key on mount and handle cache migration
  useEffect(() => {
    const initializeApp = async () => {
      try {
      refreshAdminCredentialState();
      
      // Check and perform cache migration if needed
      if (checkMigrationRequired()) {
        debugLog('Cache migration required, performing migration...');
        try {
          await performMigration();
          debugLog('Cache migration completed successfully');
        } catch (error) {
          console.error('Cache migration failed:', error);
        }
      }
      
      // Clean up expired cache entries
      try {
        const cacheManager = getCacheManager();
        const clearedCount = await cacheManager.clearExpired();
        if (clearedCount > 0) {
          debugLog(`Cleared ${clearedCount} expired cache entries on app startup`);
        }
      } catch (error) {
        console.error('Failed to clear expired cache on startup:', error);
      }
      
      } finally {
        setAppInitialized(true);
      }
    };
    
    void initializeApp().catch(error => {
      console.error('Failed to initialize application:', error);
    });
  }, [refreshAdminCredentialState]);

  // Handle API key changes
  const handleApiKeyChange = useCallback((_hasKey: boolean) => {
    refreshAdminCredentialState();
  }, [refreshAdminCredentialState]);

  // Handle notification dismissal
  const persistDismissals = useCallback((notificationIds: string[]) => {
    const dismissals = pruneDailyDismissals(
      parseStoredDailyDismissals(localStorage.getItem(DISMISSED_NOTIFICATIONS_STORAGE_KEY)),
      formatUtcDateKey(new Date())
    );
    for (const notificationId of notificationIds) {
      const notification = notificationsRef.current.find(item => item.id === notificationId);
      dismissals[notificationId] = notification?.date ?? '';
    }
    localStorage.setItem(DISMISSED_NOTIFICATIONS_STORAGE_KEY, JSON.stringify(dismissals));
  }, []);

  const handleDismissNotification = useCallback((notificationId: string) => {
    try {
      persistDismissals([notificationId]);
    } catch (cause) {
      console.warn('Failed to persist notification dismissal:', cause);
    }
    setNotifications(prev => prev.map(notification =>
      notification.id === notificationId
        ? { ...notification, dismissed: true }
        : notification
    ));
  }, [persistDismissals]);

  // Handle dismissing all notifications
  const handleDismissAllNotifications = useCallback(() => {
    try {
      persistDismissals(notificationsRef.current.map(notification => notification.id));
    } catch (cause) {
      console.warn('Failed to persist notification dismissals:', cause);
    }
    setNotifications(prev => prev.map(notification => ({ ...notification, dismissed: true })));
  }, [persistDismissals]);

  const handleDailyBudgetEvaluation = useCallback((evaluation: DailyBudgetEvaluation) => {
    const dismissals = pruneDailyDismissals(
      parseStoredDailyDismissals(localStorage.getItem(DISMISSED_NOTIFICATIONS_STORAGE_KEY)),
      evaluation.date
    );
    const previous = notificationsRef.current;
    const updated = applyDailyDismissals(
      reconcileDailyNotifications(previous, evaluation),
      dismissals
    );
    try {
      localStorage.setItem(DISMISSED_NOTIFICATIONS_STORAGE_KEY, JSON.stringify(dismissals));
      localStorage.setItem('apiKeyNotifications', JSON.stringify(updated));
    } catch (cause) {
      throw cause instanceof Error
        ? cause
        : new Error('Failed to persist daily alert notifications');
    }
    notificationsRef.current = updated;
    if (JSON.stringify(updated) !== JSON.stringify(previous)) {
      setNotifications(updated);
    }
  }, []);

  const { error: dailyMonitorError } = useDailyCostMonitor({
    enabled: appInitialized,
    hasOpenAiKey: hasValidApiKey,
    hasAnthropicKey,
    credentialRevision,
    threshold: notificationThreshold,
    onEvaluation: handleDailyBudgetEvaluation
  });

  // Persist outside state updaters so notification reconciliation stays pure.
  useEffect(() => {
    const serialized = JSON.stringify(notifications);
    try {
      if (localStorage.getItem('apiKeyNotifications') !== serialized) {
        localStorage.setItem('apiKeyNotifications', serialized);
      }
    } catch (cause) {
      console.warn('Failed to persist daily alert notifications:', cause);
    }
  }, [notifications]);

  // Keep notifications and settings coherent across same-origin browser tabs.
  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === 'apiKeyNotifications') {
        const incoming = applyDailyDismissals(
          parseStoredDailyNotifications(event.newValue),
          parseStoredDailyDismissals(localStorage.getItem(DISMISSED_NOTIFICATIONS_STORAGE_KEY))
        );
        setNotifications(previous =>
          JSON.stringify(previous) === JSON.stringify(incoming) ? previous : incoming
        );
      } else if (event.key === DISMISSED_NOTIFICATIONS_STORAGE_KEY) {
        const dismissals = parseStoredDailyDismissals(event.newValue);
        setNotifications(previous => {
          const updated = applyDailyDismissals(previous, dismissals);
          return JSON.stringify(previous) === JSON.stringify(updated) ? previous : updated;
        });
      } else if (event.key === 'notificationThreshold') {
        const parsed = Number(event.newValue);
        if (Number.isFinite(parsed) && parsed >= 0) setNotificationThreshold(parsed);
      } else if (
        event.key === OPENAI_ADMIN_API_KEY_STORAGE_KEY
        || event.key === ANTHROPIC_ADMIN_API_KEY_STORAGE_KEY
        || event.key === ADMIN_CREDENTIAL_REVISION_STORAGE_KEY
      ) {
        refreshAdminCredentialState();
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [refreshAdminCredentialState]);

  // Handle opening settings modal
  const handleOpenSettings = useCallback(() => {
    setIsSettingsModalOpen(true);
  }, []);

  // Handle timezone toggle
  const handleToggleTimezone = useCallback(() => {
    setTimezoneMode(prev => {
      const next = prev === 'utc' ? 'local' : 'utc';
      saveTimezoneMode(next);
      return next;
    });
  }, []);

  // Tab navigation handler with URL update
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    // Update URL with tab parameter
    const url = new URL(window.location.href);
    url.searchParams.set('tab', tab);
    window.history.pushState({}, '', url.toString());
  };

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const tabFromUrl = urlParams.get('tab');
      if (tabFromUrl) {
        if (TAB_REDIRECTS[tabFromUrl]) {
          setActiveTab(TAB_REDIRECTS[tabFromUrl]);
        } else if (Object.values(TABS).includes(tabFromUrl)) {
          setActiveTab(tabFromUrl);
        } else {
          setActiveTab(TABS.USAGE);
        }
      } else {
        setActiveTab(TABS.USAGE);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);


  // Listen for notification threshold changes
  useEffect(() => {
    const handleThresholdChange = () => {
      const saved = localStorage.getItem('notificationThreshold');
      if (saved) {
        const newThreshold = Number(saved);
        if (!Number.isFinite(newThreshold) || newThreshold < 0) return;
        setNotificationThreshold(newThreshold);
        
        // Clear notifications that no longer meet the new threshold
        setNotifications(prev => {
          const filtered = prev.filter(notification => {
            // Keep dismissed notifications as they are
            if (notification.dismissed) return true;
            // Keep notifications that still exceed the new threshold
            return notification.cost >= newThreshold;
          });
          return filtered;
        });
      }
    };

    window.addEventListener('notificationThresholdChanged', handleThresholdChange);
    
    
    return () => {
      window.removeEventListener('notificationThresholdChanged', handleThresholdChange);
    };
  }, []);

  // Generate breadcrumb items based on current state
  const breadcrumbItems = useMemo(() => {
    const items = [];
    const urlParams = new URLSearchParams(window.location.search);
    const view = urlParams.get('view');
    const project = urlParams.get('project');

    // Always show Dashboard as root
    items.push({
      label: 'Dashboard',
      onClick: () => {
        const url = new URL(window.location.href);
        url.search = '';
        window.history.pushState({}, '', url.toString());
        setActiveTab(TABS.USAGE);
      }
    });

    // Current tab labels
    const tabLabels: Record<string, string> = {
      [TABS.USAGE]: 'Usage Dashboard',
      [TABS.MANAGEMENT]: 'Management API',
      [TABS.GITHUB]: 'GitHub',
      [TABS.GEMINI]: 'Gemini',
      [TABS.EMPLOYEE_MAPPING]: 'Employee Mapping'
    };

    // Add provider context as intermediate breadcrumb where disambiguation matters
    if (activeTab === TABS.MANAGEMENT) {
      items.push({ label: 'OpenAI' });
    }

    // Always add current tab
    items.push({
      label: tabLabels[activeTab] || activeTab,
      isActive: !view && !project
    });

    // Add sub-views
    if (view === 'usage') {
      items.push({
        label: 'Usage Analytics',
        isActive: true
      });
    } else if (project) {
      items.push({
        label: 'Project Details',
        isActive: true
      });
    }

    return items;
  }, [activeTab]);


  // Main render
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="container mx-auto px-2 sm:px-4 py-2" style={{maxWidth:'2560px'}}>
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <span className="text-base lg:text-lg font-bold text-gray-900 dark:text-white">
                Usage Dashboard
              </span>
              <div className="hidden sm:flex items-center">
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Your comprehensive usage overview
                </p>
                {import.meta.env.VITE_COMMIT_SHA && (
                  <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                    - {import.meta.env.VITE_COMMIT_SHA.substring(0, 7)}
                  </span>
                )}
              </div>
            </div>
            
            {/* Desktop Action Buttons */}
            <div className="hidden lg:flex items-center space-x-2">
              {/* Notification Dropdown */}
              <NotificationDropdown
                notifications={notifications}
                notificationThreshold={notificationThreshold}
                onDismissNotification={handleDismissNotification}
                onDismissAll={handleDismissAllNotifications}
                monitorError={dailyMonitorError}
              />
              
              {/* Employee Mapping Icon */}
              <button
                onClick={() => handleTabChange(TABS.EMPLOYEE_MAPPING)}
                className="p-1.5 rounded-md text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title="Employee Mapping"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </button>

              {/* Theme Toggle */}
              <button
                onClick={handleToggleTheme}
                className="p-1.5 rounded-md text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title={themeMode === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              >
                {themeMode === 'dark' ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                )}
              </button>

              {/* Settings Gear Icon */}
              <button
                onClick={() => setIsSettingsModalOpen(true)}
                className="p-1.5 rounded-md text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title="Settings"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
            </div>
            
            {/* Mobile Menu Button */}
            <div className="flex lg:hidden items-center space-x-2">
              {/* Notification Dropdown (mobile) */}
              <NotificationDropdown
                notifications={notifications}
                notificationThreshold={notificationThreshold}
                onDismissNotification={handleDismissNotification}
                onDismissAll={handleDismissAllNotifications}
                monitorError={dailyMonitorError}
              />
              
              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 rounded-md text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title="Menu"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isMobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>
          
          {/* Mobile Menu Dropdown */}
          {isMobileMenuOpen && (
            <div className="lg:hidden mt-3 pb-3 border-t border-gray-200 dark:border-gray-700 pt-3">
              <div className="flex flex-col space-y-2">
                <button
                  onClick={() => {
                    handleToggleTheme();
                    setIsMobileMenuOpen(false);
                  }}
                  className="flex items-center justify-between px-3 py-2 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <span>{themeMode === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
                  {themeMode === 'dark' ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                    </svg>
                  )}
                </button>
                <button
                  onClick={() => {
                    setIsSettingsModalOpen(true);
                    setIsMobileMenuOpen(false);
                  }}
                  className="flex items-center justify-between px-3 py-2 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <span>Settings</span>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </button>
              </div>
            </div>
          )}
          
          {/* Breadcrumb Navigation */}
          <div className="hidden sm:block">
            <Breadcrumb items={breadcrumbItems} />
          </div>
          
          {/* Tab Navigation */}
          <nav className="mt-1 -mx-2 sm:-mx-4 px-2 sm:px-4 overflow-x-auto no-scrollbar">
            <div className="flex space-x-1 min-w-fit">
              <TabButton label="Usage Dashboard" isActive={activeTab === TABS.USAGE} onClick={() => handleTabChange(TABS.USAGE)} />
              <TabButton label="Management" isActive={activeTab === TABS.MANAGEMENT} onClick={() => handleTabChange(TABS.MANAGEMENT)} />
              <div className="h-6 w-px bg-gray-300 dark:bg-gray-600 mx-2 self-center" />
              <TabButton label="GitHub" isActive={activeTab === TABS.GITHUB} onClick={() => handleTabChange(TABS.GITHUB)} />
              <TabButton label="Gemini" isActive={activeTab === TABS.GEMINI} onClick={() => handleTabChange(TABS.GEMINI)} />
            </div>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-2 sm:px-3 py-2" style={{maxWidth:'2560px'}}>
        <ErrorBoundary>
          {activeTab === TABS.USAGE && (
            <SectionErrorBoundary sectionName="Usage Dashboard">
              <UsageTab hasValidApiKey={hasValidApiKey} hasAnthropicKey={hasAnthropicKey} credentialRevision={credentialRevision} onOpenSettings={handleOpenSettings} timezoneMode={timezoneMode} onToggleTimezone={handleToggleTimezone} />
            </SectionErrorBoundary>
          )}
          {activeTab === TABS.MANAGEMENT && (
            <SectionErrorBoundary sectionName="Management Dashboard">
              <ManagementAPITab hasValidApiKey={hasValidApiKey} onOpenSettings={handleOpenSettings} />
            </SectionErrorBoundary>
          )}
          {activeTab === TABS.GITHUB && (
          <div className="flex items-center justify-center min-h-[400px] lg:min-h-[500px] p-6 lg:p-8">
            <div className="max-w-md text-center">
              <div className="mb-6 lg:mb-8">
                <div className="w-20 h-20 lg:w-24 lg:h-24 mx-auto bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 rounded-full flex items-center justify-center shadow-lg">
                  <svg className="w-10 h-10 lg:w-12 lg:h-12 text-gray-700 dark:text-gray-300" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                  </svg>
                </div>
              </div>
              <h2 className="text-2xl lg:text-3xl font-bold mb-3 text-gray-900 dark:text-white">GitHub API</h2>
              <p className="text-base lg:text-lg text-gray-600 dark:text-gray-400 mb-6 leading-relaxed px-4">
                Track API usage, rate limits, and repository interactions across your organization
              </p>
              <div className="inline-flex items-center px-5 lg:px-6 py-2.5 lg:py-3 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 text-gray-700 dark:text-gray-300 rounded-full border border-gray-200 dark:border-gray-600 shadow-sm text-sm lg:text-base">
                <svg className="w-4 h-4 lg:w-5 lg:h-5 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-medium">Coming Soon</span>
              </div>
            </div>
          </div>
        )}
        {activeTab === TABS.GEMINI && (
          <div className="flex items-center justify-center min-h-[400px] lg:min-h-[500px] p-6 lg:p-8">
            <div className="max-w-md text-center">
              <div className="mb-6 lg:mb-8">
                <div className="w-20 h-20 lg:w-24 lg:h-24 mx-auto bg-gradient-to-br from-blue-100 to-indigo-200 dark:from-blue-900/30 dark:to-indigo-800/30 rounded-full flex items-center justify-center shadow-lg">
                  <svg className="w-10 h-10 lg:w-12 lg:h-12 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
              </div>
              <h2 className="text-2xl lg:text-3xl font-bold mb-3 text-gray-900 dark:text-white">Google Gemini API</h2>
              <p className="text-base lg:text-lg text-gray-600 dark:text-gray-400 mb-6 leading-relaxed px-4">
                Advanced multimodal AI usage analytics and performance monitoring for Gemini models
              </p>
              <div className="inline-flex items-center px-5 lg:px-6 py-2.5 lg:py-3 bg-gradient-to-r from-blue-50 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-800/20 text-blue-700 dark:text-blue-300 rounded-full border border-blue-200 dark:border-blue-700 shadow-sm text-sm lg:text-base">
                <svg className="w-4 h-4 lg:w-5 lg:h-5 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-medium">Coming Soon</span>
              </div>
            </div>
          </div>
          )}
          {activeTab === TABS.EMPLOYEE_MAPPING && (
            <SectionErrorBoundary sectionName="Employee Mapping">
              <EmployeeMapping />
            </SectionErrorBoundary>
          )}
        </ErrorBoundary>
      </main>

      {/* Settings Modal */}
      {isSettingsModalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/55 flex items-end sm:items-center justify-center sm:p-3"
          role="dialog"
          aria-modal="true"
          aria-labelledby="settings-title"
          onClick={() => setIsSettingsModalOpen(false)}
          onKeyDown={(e) => { if (e.key === 'Escape') setIsSettingsModalOpen(false); }}
        >
          <div 
            className="bg-white dark:bg-gray-800 w-full h-[94vh] sm:h-auto sm:max-h-[92vh] sm:max-w-6xl rounded-t-xl sm:rounded-xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 z-10 border-b border-gray-200 dark:border-gray-700 bg-white/95 dark:bg-gray-800/95 backdrop-blur px-4 sm:px-5 py-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 id="settings-title" className="text-base font-semibold text-gray-900 dark:text-white">Settings</h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    API keys, usage behavior, and dashboard metadata.
                  </p>
                </div>
                <button
                  onClick={() => setIsSettingsModalOpen(false)}
                  className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  aria-label="Close settings"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="max-h-[calc(94vh-72px)] sm:max-h-[calc(92vh-72px)] overflow-y-auto custom-scrollbar">
              <div className="px-2 py-3 sm:px-3">
                <ApiKeySettings onApiKeyChange={handleApiKeyChange} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;
